/* Tora Moto Studio theme. Vanilla JS, no build step. The WebGL pieces (loader, glass claw, footer emblem)
   are separate module islands built from the React project (toramoto-v2: npm run islands). */
(() => {
  const TM = window.TM || {};
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reduce = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const track = (event, data = {}) => (window.dataLayer = window.dataLayer || []).push({ event, ...data });

  // ---------- money ----------
  const fmt = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
  const money = (cents) => {
    const f = TM.money || '₹{{amount_no_decimals}}';
    const amount = fmt.format(Math.round(cents / 100));
    return f.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, amount).replace(/<[^>]*>/g, '');
  };

  // ---------- scroll lock ----------
  let locks = 0;
  const lock = (on) => {
    locks = Math.max(0, locks + (on ? 1 : -1));
    document.documentElement.style.overflow = locks ? 'hidden' : '';
  };

  // ---------- delivery estimate (IST, Mon–Sat) ----------
  function arrival() {
    const s = TM.ship || { min: 1, max: 3, dmin: 5, dmax: 7, cutoff: 14 };
    const now = new Date();
    const ist = new Date(now.getTime() + now.getTimezoneOffset() * 60000 + 330 * 60000);
    const late = ist.getHours() >= s.cutoff ? 1 : 0;
    const add = (n) => {
      const d = new Date(ist);
      let left = n;
      while (left > 0) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0) left--;
      }
      return d;
    };
    const f = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' });
    return `${f.format(add(s.min + late + s.dmin))} – ${f.format(add(s.max + late + s.dmax))}`;
  }
  const fillArrival = (root = document) => $$('[data-arrival]', root).forEach((el) => (el.textContent = arrival()));

  // ---------- header ----------
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const start = header.dataset.startTone;
    const menu = $('[data-menu]');
    const toggle = $('[data-menu-toggle]');
    let open = false;
    const paint = () => {
      const y = window.scrollY;
      header.style.setProperty('--hy', `${Math.max(0, 32 - y)}px`);
      if (start === 'hero') header.dataset.tone = y > 40 || open ? 'light' : 'hero';
    };
    addEventListener('scroll', paint, { passive: true });
    paint();
    const setMenu = (v) => {
      if (!menu || v === open) return;
      open = v;
      toggle.setAttribute('aria-expanded', String(v));
      toggle.setAttribute('aria-label', v ? 'Close menu' : 'Open menu');
      menu.setAttribute('aria-hidden', String(!v));
      lock(v);
      paint();
    };
    toggle?.addEventListener('click', () => setMenu(!open));
    addEventListener('keydown', (e) => e.key === 'Escape' && setMenu(false));
    $$('a', menu || document.createElement('div')).forEach((a) => a.addEventListener('click', () => setMenu(false)));
    $$('[data-search-open]', menu || document.createElement('div')).forEach((b) => b.addEventListener('click', () => setMenu(false)));
  }

  // ---------- announcement bar ----------
  function initAnnounce(root = document) {
    $$('[data-announce]', root).forEach((bar) => {
      const items = $$('[data-announce-item]', bar);
      if (items.length < 2) return;
      let i = 0;
      const show = (n) => {
        items[i].classList.add('-translate-y-2.5', 'opacity-0');
        items[i].setAttribute('aria-hidden', 'true');
        i = (n + items.length) % items.length;
        items[i].classList.remove('translate-y-2.5', '-translate-y-2.5', 'opacity-0');
        items[i].removeAttribute('aria-hidden');
        items.forEach((el, k) => k !== i && setTimeout(() => el.classList.replace('-translate-y-2.5', 'translate-y-2.5'), 320));
      };
      let t = setInterval(() => show(i + 1), Number(bar.dataset.interval) || 5000);
      const reset = () => {
        clearInterval(t);
        t = setInterval(() => show(i + 1), Number(bar.dataset.interval) || 5000);
      };
      $('[data-announce-prev]', bar)?.addEventListener('click', () => (show(i - 1), reset()));
      $('[data-announce-next]', bar)?.addEventListener('click', () => (show(i + 1), reset()));
    });
  }

  // ---------- running strips ----------
  function initMarquees(root = document) {
    $$('[data-marquee]', root).forEach((m) => {
      const strip = $('.marquee', m);
      const btn = $('[data-marquee-toggle]', m);
      btn?.addEventListener('click', () => {
        const paused = strip.dataset.paused !== 'true';
        strip.dataset.paused = String(paused);
        btn.setAttribute('aria-label', paused ? 'Play the strip' : 'Pause the strip');
        $('[data-on]', btn).hidden = paused;
        $('[data-off]', btn).hidden = !paused;
      });
    });
  }

  // ---------- reveal on scroll ----------
  let revealer;
  function initReveal(root = document) {
    const els = $$('[data-reveal]:not(.is-in)', root);
    if (reduce() || !('IntersectionObserver' in window) || TM.design) return els.forEach((e) => e.classList.add('is-in'));
    revealer ??= new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (e.target.classList.add('is-in'), revealer.unobserve(e.target))),
      { rootMargin: '0px 0px -8% 0px' },
    );
    els.forEach((e) => revealer.observe(e));
  }

  // ---------- films (hero + bands) ----------
  function initFilms(root = document) {
    $$('[data-film]', root).forEach((sec) => {
      const v = $('[data-film-video]', sec);
      if (!v || v.dataset.ready) return;
      v.dataset.ready = '1';
      const saveData = navigator.connection && navigator.connection.saveData;
      // upright phones get the 4:5 centre crop when there is one (same framing, a third of the bytes)
      const upright = innerWidth < 1024 && innerHeight > innerWidth * 1.1;
      const src = innerWidth >= 1024 && !saveData ? v.dataset.srcHd : (upright && v.dataset.srcPortrait) || v.dataset.srcSd;
      const btn = $('[data-film-toggle]', sec);
      let want = !reduce();
      const paint = () => {
        if (!btn) return;
        $('[data-film-pause]', btn).hidden = !want;
        $('[data-film-play]', btn).hidden = want;
        btn.setAttribute('aria-label', want ? 'Pause film' : 'Play film');
      };
      const lazy = sec.hasAttribute('data-lazy-film');
      let visible = !lazy;
      const sync = () => {
        if (!v.src) return;
        if (want && visible) v.play().catch(() => ((want = false), paint()));
        else v.pause();
      };
      if (!lazy) {
        // the loader gets the bandwidth first: the film starts when it clears (or after 4s at most)
        const start = () => {
          if (v.src) return;
          v.src = src;
          sync();
        };
        if (document.documentElement.classList.contains('tm-intro') && !window.TMIntroDone) {
          window.addEventListener('tm:intro-done', start, { once: true });
          setTimeout(start, 4000);
        } else if (innerWidth < 1024 && document.readyState !== 'complete') {
          // phones: the poster holds the screen while the page's images load, then the film takes over
          addEventListener('load', start, { once: true });
          setTimeout(start, 3000);
        } else start();
      } else if ('IntersectionObserver' in window) {
        new IntersectionObserver(([e]) => e.isIntersecting && !v.src && ((v.src = src), sync()), { rootMargin: '400px 0px' }).observe(sec);
        new IntersectionObserver(([e]) => ((visible = e.intersectionRatio >= 0.3), sync()), { threshold: [0, 0.3] }).observe(sec);
      } else {
        v.src = src;
      }
      btn?.addEventListener('click', () => {
        want = !want;
        visible = true;
        paint();
        sync();
      });
      paint();
    });
  }

  // ---------- rails ----------
  function initRails(root = document) {
    $$('[data-rail]', root).forEach((r) => {
      const t = $('[data-rail-track]', r);
      if (!t) return;
      $('[data-rail-prev]', r)?.addEventListener('click', () => t.scrollBy({ left: -t.clientWidth * 0.8, behavior: 'smooth' }));
      $('[data-rail-next]', r)?.addEventListener('click', () => t.scrollBy({ left: t.clientWidth * 0.8, behavior: 'smooth' }));
    });
  }

  // ---------- product cards ----------
  function initCards(root = document) {
    $$('[data-card]', root).forEach((card) => {
      if (card.dataset.ready) return;
      card.dataset.ready = '1';
      const t = $('[data-card-track]', card);
      const dots = $$('[data-card-dots] span', card);
      const more = $('template[data-card-more]', card);
      if (t && more) {
        const fill = () => {
          if (!more.isConnected) return;
          t.append(more.content.cloneNode(true));
          more.remove();
        };
        if ('IntersectionObserver' in window) {
          const io = new IntersectionObserver(([e]) => e.isIntersecting && (io.disconnect(), fill()), { rootMargin: '120px 0px' });
          io.observe(card);
        } else fill();
        ['pointerenter', 'touchstart', 'focusin'].forEach((ev) => card.addEventListener(ev, fill, { once: true, passive: true }));
      }
      if (t && dots.length) {
        let idx = 0;
        t.addEventListener(
          'scroll',
          () => {
            const n = Math.round(t.scrollLeft / t.clientWidth);
            if (n === idx) return;
            dots.forEach((d, k) => (d.className = `h-1 rounded-full transition-all duration-300 ${k === n ? d.dataset.on : d.dataset.off}`));
            idx = n;
          },
          { passive: true },
        );
        const go = (d) => t.scrollTo({ left: Math.max(0, Math.min(dots.length - 1, idx + d)) * t.clientWidth, behavior: 'smooth' });
        $('[data-card-prev]', card)?.addEventListener('click', () => go(-1));
        $('[data-card-next]', card)?.addEventListener('click', () => go(1));
      }
      const pick = $('[data-card-pick]', card);
      const openBtn = $('[data-card-pick-open]', card);
      const setPick = (v) => {
        if (!pick) return;
        pick.setAttribute('aria-hidden', String(!v));
        openBtn?.setAttribute('aria-expanded', String(v));
      };
      openBtn?.addEventListener('click', () => setPick(pick.getAttribute('aria-hidden') === 'true'));
      $('[data-card-pick-close]', card)?.addEventListener('click', () => setPick(false));
      $$('[data-add-variant]', pick || card).forEach((b) => b.addEventListener('add:done', () => setPick(false)));
    });
  }

  // ---------- cart ----------
  const cart = {
    root: () => $('[data-cart-root]'),
    async request(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: 'cart-drawer', sections_url: location.pathname }),
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message || 'Could not update the bag');
      if (json.sections && json.sections['cart-drawer']) this.render(json.sections['cart-drawer']);
      return json;
    },
    render(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('[data-cart-root]', doc);
      const old = this.root();
      if (!fresh || !old) return;
      // lines that weren't there before slide in, so the shopper sees what just landed
      const had = new Set($$('[data-key]', old).map((li) => li.dataset.key));
      if (had.size || Number(old.dataset.count) === 0) $$('[data-key]', fresh).forEach((li) => had.has(li.dataset.key) || li.classList.add('tm-new'));
      // keep the drawer open across the swap, without replaying its slide-in
      if (this.isOpen()) $$('[data-cart-drawer], [data-cart-scrim]', fresh).forEach((el) => el.setAttribute('aria-hidden', 'false'));
      old.replaceWith(fresh);
      this.wire();
    },
    isOpen: () => $('[data-cart-drawer]')?.getAttribute('aria-hidden') === 'false',
    open(silent) {
      const d = $('[data-cart-drawer]');
      if (!d || this.isOpen()) return;
      d.setAttribute('aria-hidden', 'false');
      $('[data-cart-scrim]')?.setAttribute('aria-hidden', 'false');
      if (!silent) lock(true);
      $('[data-cart-close]')?.focus();
    },
    close() {
      if (!this.isOpen()) return;
      $('[data-cart-drawer]').setAttribute('aria-hidden', 'true');
      $('[data-cart-scrim]')?.setAttribute('aria-hidden', 'true');
      lock(false);
    },
    async add(items, source, btn) {
      try {
        btn?.setAttribute('aria-busy', 'true');
        await this.request(TM.routes.cartAdd + '.js', { items });
        track('add_to_cart', { source, items: items.length });
        btn?.dispatchEvent(new Event('add:done'));
        this.open();
      } catch (e) {
        toast(e.message);
      } finally {
        btn?.removeAttribute('aria-busy');
      }
    },
    async change(key, quantity) {
      $('[data-cart-drawer]')?.setAttribute('aria-busy', 'true');
      try {
        await this.request(TM.routes.cartChange + '.js', { id: key, quantity });
      } catch (e) {
        toast(e.message);
      } finally {
        $('[data-cart-drawer]')?.removeAttribute('aria-busy');
      }
    },
    // one code goes to checkout: whichever saves more (the kit code only once it exists in Discounts)
    totals() {
      const r = this.root();
      if (!r) return null;
      const locked = r.dataset.locked === 'true';
      const toggle = $('[data-prepaid-toggle]', r);
      let wantPrepaid = true;
      try {
        wantPrepaid = localStorage.getItem('tm-prepaid') !== '0';
      } catch (e) {}
      const prepaidOn = locked || (toggle ? wantPrepaid : true);
      if (toggle) toggle.checked = prepaidOn;
      const prepaid = prepaidOn && TM.prepaid.on ? Number(r.dataset.prepaidSaving) : 0;
      const kit = TM.kit.on && TM.kit.confirmed ? Number(r.dataset.kitSaving) : 0;
      const useKit = kit > 0 && kit >= prepaid;
      const code = useKit ? TM.kit.code : prepaid > 0 ? TM.prepaid.code : null;
      const saving = useKit ? kit : prepaid;
      return { code, saving, useKit, subtotal: Number(r.dataset.subtotal) };
    },
    paintTotals() {
      const r = this.root();
      const t = this.totals();
      if (!r || !t) return;
      const row = $('[data-code-row]', r);
      if (row) {
        row.hidden = !t.code;
        $('[data-code-label]', r).textContent = t.code ? `${t.useKit ? 'Carry Kit' : 'Prepaid'} (${t.code})` : '';
        $('[data-code-saving]', r).textContent = t.code ? `−${money(t.saving)}` : '';
      }
      const total = $('[data-total]', r);
      if (total) total.textContent = money(t.subtotal - t.saving);
      // the prepaid code only applies when paying online, so the total says so
      const tl = $('[data-total-label]', r);
      if (tl) tl.textContent = t.code && !t.useKit ? 'Total, paid online' : 'Total';
      const note = $('[data-prepaid-note]', r);
      const save = $('[data-you-save]', r);
      const all = t.saving + Number(r.dataset.mrpSaving || 0);
      if (save) {
        save.hidden = all <= 0;
        save.textContent = `You save ${money(all)} on this order`;
      }
      if (note && t.useKit) note.textContent = 'The Carry Kit code saves you more, so checkout uses that one.';
    },
    wire() {
      const r = this.root();
      if (!r) return;
      const count = Number(r.dataset.count || 0);
      $$('[data-cart-count]').forEach((b) => {
        if (count > Number(b.textContent || 0)) b.animate?.([{ transform: 'scale(1.6)' }, { transform: 'scale(1)' }], { duration: 450, easing: 'cubic-bezier(.16,1,.3,1)' });
        b.textContent = count;
        b.classList.toggle('hidden', count === 0);
        b.classList.toggle('grid', count > 0);
      });
      $$('[data-cart-open]').forEach((a) => a.setAttribute('aria-label', `Bag, ${count} item${count === 1 ? '' : 's'}`));
      $('[data-cart-close]', r)?.addEventListener('click', () => this.close());
      $('[data-cart-scrim]', r)?.addEventListener('click', () => this.close());
      $$('[data-line]', r).forEach((b) => b.addEventListener('click', () => this.change(b.dataset.line, Number(b.dataset.qty))));
      $('[data-prepaid-toggle]', r)?.addEventListener('change', (e) => {
        try {
          localStorage.setItem('tm-prepaid', e.target.checked ? '1' : '0');
        } catch (err) {}
        this.paintTotals();
      });
      $('[data-checkout-form]', r)?.addEventListener('submit', (e) => {
        e.preventDefault();
        const t = this.totals();
        track('begin_checkout', { value: t ? (t.subtotal - t.saving) / 100 : 0, code: t?.code || 'none' });
        $('[data-leaving]', r)?.setAttribute('aria-hidden', 'false');
        location.href = '/checkout' + (t && t.code ? `?discount=${encodeURIComponent(t.code)}` : '');
      });
      fillArrival(r);
      this.paintTotals();
    },
  };
  addEventListener('pageshow', () => $('[data-leaving]')?.setAttribute('aria-hidden', 'true'));
  addEventListener('keydown', (e) => e.key === 'Escape' && cart.close());

  function toast(msg) {
    let t = $('#tm-toast');
    if (!t) {
      t = document.createElement('div');
      t.id = 'tm-toast';
      t.setAttribute('role', 'alert');
      t.className = 'fixed inset-x-4 bottom-4 z-[95] mx-auto max-w-md rounded-2xl bg-ink px-5 py-4 text-sm text-white shadow-lg transition-opacity';
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.style.opacity = '1';
    clearTimeout(t._h);
    t._h = setTimeout(() => (t.style.opacity = '0'), 3500);
  }

  // add buttons anywhere (cards, kit thumbnails, stories)
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-add-variant]');
    if (b) {
      e.preventDefault();
      cart.add([{ id: Number(b.dataset.addVariant), quantity: 1 }], b.dataset.source || 'button', b);
      return;
    }
    const open = e.target.closest('[data-cart-open]');
    if (open && $('[data-cart-drawer]')) {
      e.preventDefault();
      cart.open();
    }
  });

  // ---------- wishlist (on this device) ----------
  const saved = {
    get() {
      try {
        return JSON.parse(localStorage.getItem('tm-saved') || '[]');
      } catch (e) {
        return [];
      }
    },
    set(list) {
      try {
        localStorage.setItem('tm-saved', JSON.stringify(list));
      } catch (e) {}
      this.paint();
    },
    toggle(h) {
      const l = this.get();
      const on = l.includes(h);
      this.set(on ? l.filter((x) => x !== h) : [h, ...l]);
      track(on ? 'wishlist_remove' : 'wishlist_add', { item: h });
    },
    paint(root = document) {
      const l = this.get();
      $$('[data-save]', root).forEach((b) => {
        const on = l.includes(b.dataset.save);
        b.setAttribute('aria-pressed', String(on));
        b.setAttribute('aria-label', `${on ? 'Remove' : 'Save'} ${b.dataset.name}${on ? ' from Saved' : ''}`);
        $('.tm-heart-on', b)?.classList.toggle('hidden', !on);
        $('.tm-heart-off', b)?.classList.toggle('hidden', on);
      });
      $$('[data-saved-count]').forEach((c) => {
        c.textContent = l.length;
        c.classList.toggle('hidden', !l.length);
        c.classList.toggle('grid', !!l.length);
      });
      const page = $('[data-saved-page]');
      if (page) {
        let n = 0;
        $$('[data-saved-item]', page).forEach((li) => {
          const on = l.includes(li.dataset.savedItem);
          li.hidden = !on;
          if (on) n++;
        });
        $('[data-saved-grid]', page).hidden = !n;
        $('[data-saved-empty]', page).hidden = !!n;
        const s = $('[data-saved-summary]', page);
        s.textContent = n ? s.dataset.some.replace('[count]', `${n} piece${n === 1 ? '' : 's'}`) : s.dataset.none;
      }
    },
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-save]');
    if (!b) return;
    e.preventDefault();
    e.stopPropagation();
    saved.toggle(b.dataset.save);
  });

  // ---------- search ----------
  function initSearch() {
    const box = $('[data-search]');
    if (!box) return;
    const input = $('[data-search-input]', box);
    const list = $('[data-search-results]', box);
    const suggest = $('[data-search-suggest]', box);
    const set = (v) => {
      box.setAttribute('aria-hidden', String(!v));
      lock(v);
      if (v) setTimeout(() => input.focus(), 60);
      else {
        input.value = '';
        list.innerHTML = '';
        suggest.hidden = false;
      }
    };
    $$('[data-search-open]').forEach((b) => b.addEventListener('click', () => set(true)));
    $('[data-search-close]', box)?.addEventListener('click', () => set(false));
    box.addEventListener('keydown', (e) => e.key === 'Escape' && set(false));
    addEventListener('keydown', (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        set(box.getAttribute('aria-hidden') === 'true');
      }
    });
    let timer;
    // Theme settings → Brand spelling, as the server-rendered pages apply it
    const brand = (t) => (TM.brand || '').split('\n').reduce((s, r) => { const [a, b] = r.split('=>').map((x) => (x || '').trim()); return a && b ? s.split(a).join(b) : s; }, t);
    const run = async () => {
      const q = input.value.trim();
      suggest.hidden = !!q;
      if (!q) return (list.innerHTML = '');
      try {
        const res = await fetch(`${TM.routes.search}.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8`);
        if (!res.ok) throw new Error(res.status);
        const json = await res.json();
        if (q !== input.value.trim()) return; // a newer search is on its way
        const items = json.resources?.results?.products || [];
        list.innerHTML = items.length
          ? items
              .map(
                (p) => `<li><a href="${p.url}" class="flex w-full items-center gap-4 rounded-2xl p-2 text-left transition hover:bg-white/80">
                  <img src="${(window.TM.packs || {})[p.handle] || p.image || p.featured_image?.url || ''}" alt="" class="size-14 rounded-xl bg-stone-2 object-cover">
                  <span class="min-w-0 flex-1"><span class="block truncate font-medium">${brand(p.title)}</span>
                  <span class="tabular text-xs text-ink-2">${p.type || ''} · ${p.available ? money(Math.round(Number(p.price) * 100)) : 'Sold out'}</span></span></a></li>`,
              )
              .join('')
          : `<li class="px-2 py-6 text-ink-2">Nothing matches “${q.replace(/[<>&"]/g, '')}”. Try “backpack” or “tee”.</li>`;
        track('search', { q });
      } catch (e) {
        // suggestions are busy or down: the full search page still works
        list.innerHTML = `<li><a href="${TM.routes.root.replace(/\/$/, '')}/search?q=${encodeURIComponent(q)}" class="block px-2 py-6 font-medium underline underline-offset-4">See results for “${q.replace(/[<>&"]/g, '')}”</a></li>`;
      }
    };
    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(run, 180);
    });
    $$('[data-search-word]', box).forEach((b) => b.addEventListener('click', () => ((input.value = b.dataset.searchWord), run(), input.focus())));
  }

  // ---------- stories ----------
  function initStories(root = document) {
    $$('[data-stories]', root).forEach((wrap) => {
      const data = JSON.parse($('[data-stories-json]', wrap).textContent || '[]');
      const groups = [];
      data.forEach((s) => {
        let g = groups.find((x) => x.name === s.story);
        if (!g) groups.push((g = { name: s.story, slides: [] }));
        g.slides.push(s);
      });
      const viewer = wrap.nextElementSibling?.matches('[data-story-viewer]') ? wrap.nextElementSibling : null;
      if (!viewer || !groups.length) return;
      const seenKey = 'tm-stories-seen';
      const seen = () => {
        try {
          return JSON.parse(localStorage.getItem(seenKey) || '[]');
        } catch (e) {
          return [];
        }
      };
      const markSeen = (id) => {
        try {
          localStorage.setItem(seenKey, JSON.stringify([...new Set([...seen(), id])]));
        } catch (e) {}
      };
      const paintRings = () =>
        $$('[data-story-open]', wrap).forEach((b) => {
          const ring = $('.tm-ring', b);
          const fresh = !seen().includes(b.dataset.storyId);
          ring.classList.toggle('bg-ink/15', !fresh);
          ring.classList.toggle('bg-[conic-gradient(from_200deg,#c5202e,#dcd3c6,#c5202e)]', fresh);
        });
      paintRings();
      // links like #story-founders open that story here instead of leaving the site
      document.addEventListener('click', (e) => {
        const a = e.target.closest('[data-story-jump]');
        if (!a) return;
        const b = $(`[data-story-open][data-story-id="${a.dataset.storyJump}"]`, wrap);
        if (!b) return;
        e.preventDefault();
        b.click();
      });
      const SLIDE = 5200;
      let gi = 0, si = 0, paused = false, raf = 0, elapsed = 0, last = 0, lastFocus = null;
      const img = $('[data-story-img]', viewer);
      const vid = $('[data-story-video]', viewer);
      const bars = $('[data-story-bars]', viewer);
      const bottom = $('[data-story-bottom]', viewer);
      const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
      const render = () => {
        const g = groups[gi];
        const s = g.slides[si];
        markSeen($$('[data-story-open]', wrap)[gi]?.dataset.storyId);
        $('[data-story-label]', viewer).textContent = g.name;
        $('[data-story-count]', viewer).textContent = `${si + 1}/${g.slides.length}`;
        viewer.setAttribute('aria-label', `${g.name} story`);
        img.classList.remove('tm-kenburns');
        void img.offsetWidth;
        img.src = s.image;
        img.alt = s.caption || `${g.name} story image`;
        if (vid) {
          if (s.video && !reduce()) {
            vid.poster = s.image || '';
            if (vid.getAttribute('src') !== s.video) vid.src = s.video;
            vid.currentTime = 0;
            vid.classList.remove('hidden');
            vid.play().catch(() => {});
          } else {
            vid.pause();
            vid.classList.add('hidden');
          }
        }
        if (!reduce() && !s.video) img.classList.add('tm-kenburns');
        bars.innerHTML = g.slides
          .map((_, k) => `<span class="h-[3px] flex-1 overflow-hidden rounded-full bg-white/25"><span class="block h-full bg-white ${k < si ? '' : 'tm-bar'}" ${k === si ? 'data-bar' : ''}></span></span>`)
          .join('');
        let html = '';
        if (s.tag) html += `<span class="eyebrow !bg-white/15 !text-white !shadow-none">${esc(s.tag)}</span>`;
        if (s.caption) html += `<p class="max-w-[32ch] text-[17px] font-medium leading-snug text-white">${esc(s.caption)}</p>`;
        if (s.product) {
          const p = s.product;
          const btn = !p.available
            ? `<span class="tabular rounded-full bg-stone px-3 py-2 text-[11px] uppercase tracking-wider text-ink-2">Sold out</span>`
            : p.variant
              ? `<button type="button" data-add-variant="${p.variant}" data-source="story" class="h-11 rounded-full bg-lacquer px-4 text-sm font-medium text-white">Add</button>`
              : `<a href="${p.url}" class="inline-flex h-11 items-center rounded-full bg-ink px-4 text-sm font-medium text-stone">Pick Size</a>`;
          html += `<div class="flex items-center gap-3 rounded-[1.25rem] bg-white/95 p-2 pr-2.5 text-ink shadow-[0_20px_50px_-20px_rgb(0_0_0/0.6)]">
            <img src="${p.image}" alt="" class="size-14 shrink-0 rounded-[0.9rem] bg-stone object-cover">
            <a href="${p.url}" class="min-w-0 flex-1"><p class="truncate text-sm font-medium">${esc(p.title)}</p><p class="tabular text-[13px]">${esc(p.price)}</p></a>${btn}</div>`;
        }
        if (s.link && s.link.url) {
          const ext = /^https?:/.test(s.link.url) && !s.link.url.includes(location.host);
          html += `<a href="${s.link.url}" ${ext ? 'target="_blank" rel="noopener"' : ''} class="inline-flex h-12 items-center rounded-full bg-white px-5 text-sm font-medium text-ink">${esc(s.link.label)}</a>`;
        }
        bottom.innerHTML = html;
        elapsed = 0;
        last = performance.now();
      };
      const loop = (t) => {
        if (!paused) elapsed += t - last;
        last = t;
        const k = Math.min(1, elapsed / SLIDE);
        const bar = $('[data-bar]', bars);
        if (bar) bar.style.transform = `scaleX(${k})`;
        if (k >= 1) return next();
        raf = requestAnimationFrame(loop);
      };
      const start = () => {
        cancelAnimationFrame(raf);
        render();
        raf = requestAnimationFrame(loop);
      };
      const close = () => {
        cancelAnimationFrame(raf);
        vid?.pause();
        viewer.setAttribute('aria-hidden', 'true');
        lock(false);
        paintRings();
        lastFocus?.focus?.();
      };
      const next = () => {
        if (si < groups[gi].slides.length - 1) si++;
        else if (gi < groups.length - 1) (gi++, (si = 0));
        else return close();
        start();
      };
      const prev = () => {
        if (si > 0) si--;
        else if (gi > 0) (gi--, (si = 0));
        start();
      };
      const setPaused = (v) => {
        paused = v;
        if (vid && !vid.classList.contains('hidden')) v ? vid.pause() : vid.play().catch(() => {});
        const b = $('[data-story-pause]', viewer);
        $('[data-on]', b).hidden = v;
        $('[data-off]', b).hidden = !v;
        b.setAttribute('aria-label', v ? 'Play' : 'Pause');
      };
      $$('[data-story-open]', wrap).forEach((b) =>
        b.addEventListener('click', () => {
          lastFocus = b;
          gi = Number(b.dataset.storyOpen);
          si = 0;
          viewer.setAttribute('aria-hidden', 'false');
          lock(true);
          setPaused(false);
          start();
          $('[data-story-close]', viewer).focus();
          track('story_open', { story: groups[gi].name });
        }),
      );
      $('[data-story-close]', viewer).addEventListener('click', close);
      $('[data-story-next]', viewer).addEventListener('click', next);
      $('[data-story-prev]', viewer).addEventListener('click', prev);
      $('[data-story-back]', viewer).addEventListener('click', prev);
      const fwd = $('[data-story-fwd]', viewer);
      fwd.addEventListener('click', next);
      fwd.addEventListener('pointerdown', () => setPaused(true));
      ['pointerup', 'pointerleave'].forEach((ev) => fwd.addEventListener(ev, () => setPaused(false)));
      $('[data-story-pause]', viewer).addEventListener('click', () => setPaused(!paused));
      viewer.addEventListener('click', (e) => e.target.closest('[data-add-variant]') && close());
      addEventListener('keydown', (e) => {
        if (viewer.getAttribute('aria-hidden') !== 'false') return;
        if (e.key === 'Escape') close();
        if (e.key === 'ArrowRight') next();
        if (e.key === 'ArrowLeft') prev();
        if (e.key === ' ') (e.preventDefault(), setPaused(!paused));
      });
    });
  }

  // ---------- campaign slides ----------
  function initSlides(root = document) {
    $$('[data-slides]', root).forEach((sec) => {
      const slides = $$('[data-slide]', sec);
      const dots = $$('[data-slide-dot]', sec);
      if (slides.length < 2) return;
      let i = 0, hold = false;
      const show = (n) => {
        i = (n + slides.length) % slides.length;
        slides.forEach((s, k) => (s.classList.toggle('opacity-0', k !== i), k === i ? s.removeAttribute('aria-hidden') : s.setAttribute('aria-hidden', 'true')));
        dots.forEach((d, k) => {
          d.setAttribute('aria-selected', String(k === i));
          d.firstElementChild.className = `block h-1.5 rounded-full bg-white transition-all duration-500 ${k === i ? 'w-6' : 'w-1.5 opacity-50'}`;
        });
      };
      dots.forEach((d) => d.addEventListener('click', () => show(Number(d.dataset.slideDot))));
      sec.addEventListener('mouseenter', () => (hold = true));
      sec.addEventListener('mouseleave', () => (hold = false));
      if (!reduce()) setInterval(() => !hold && show(i + 1), Number(sec.dataset.interval) || 5200);
    });
  }

  // ---------- Carry Kit ----------
  function initKit(root = document) {
    $$('[data-kit]', root).forEach((kit) => {
      const pct = Number(kit.dataset.pct) || 15;
      const L = JSON.parse($('[data-kit-labels]', kit).innerHTML);
      const sides = {};
      let size = null;
      const addBtn = $('[data-kit-add]', kit);
      const paint = () => {
        const b = sides.bag, t = sides.tee;
        if (!b || !t) return;
        const sum = Number(b.dataset.price) + Number(t.dataset.price);
        const save = Math.round((sum * pct) / 10000) * 100; // whole rupees
        $('[data-kit-sum]', kit).innerHTML = `${b.dataset.money} + ${t.dataset.money} = <span class="line-through">${money(sum)}</span>`;
        $('[data-kit-total]', kit).textContent = `${L.kit} ${money(sum - save)}`;
        $('[data-kit-save]', kit).textContent = `· ${L.save} ${money(save)}`;
        addBtn.disabled = !size;
        addBtn.textContent = size ? L.add : L.pick;
      };
      const choose = (side, opt) => {
        sides[side] = opt;
        const box = $(`[data-kit-side="${side}"]`, kit);
        $$('[data-kit-option]', box).forEach((o) => o.setAttribute('aria-checked', String(o === opt)));
        const img = $('[data-kit-img]', box);
        img.src = opt.dataset.image;
        img.alt = opt.dataset.title;
        $('[data-kit-title]', box).textContent = opt.dataset.title;
        $('[data-kit-price]', box).textContent = opt.dataset.money;
        if (side === 'tee') {
          size = null;
          const vs = JSON.parse(opt.dataset.variants);
          const wrapS = $('[data-kit-sizes]', kit);
          wrapS.innerHTML = vs
            .map(
              (v) => `<button type="button" role="radio" aria-checked="false" data-v="${v.id}" ${v.available ? '' : 'disabled'} class="tabular h-11 min-w-11 rounded-full bg-stone px-3 text-xs ring-1 ring-line transition-colors duration-300 hover:ring-ink/30 aria-checked:bg-ink aria-checked:text-stone disabled:text-ink/30 disabled:line-through">${v.title}</button>`,
            )
            .join('');
          $$('[data-v]', wrapS).forEach((b) =>
            b.addEventListener('click', () => {
              size = Number(b.dataset.v);
              $$('[data-v]', wrapS).forEach((x) => x.setAttribute('aria-checked', String(x === b)));
              paint();
            }),
          );
        }
        paint();
      };
      ['bag', 'tee'].forEach((side) => {
        const box = $(`[data-kit-side="${side}"]`, kit);
        const opts = $$('[data-kit-option]', box);
        opts.forEach((o) => o.addEventListener('click', () => choose(side, o)));
        if (opts[0]) choose(side, opts[0]);
      });
      addBtn.addEventListener('click', () => {
        const b = sides.bag;
        if (!b || !size) return;
        const bv = JSON.parse(b.dataset.variants).find((v) => v.available) || JSON.parse(b.dataset.variants)[0];
        cart.add([{ id: bv.id, quantity: 1 }, { id: size, quantity: 1 }], 'kit', addBtn);
        track('add_kit');
      });
    });
  }

  // ---------- countdown ----------
  function initCountdowns(root = document) {
    $$('[data-countdown]', root).forEach((el) => {
      const at = new Date(el.dataset.countdown).getTime();
      if (!at) return;
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const s = Math.max(0, Math.floor((at - Date.now()) / 1000));
        const v = { days: Math.floor(s / 86400), hrs: Math.floor((s % 86400) / 3600), min: Math.floor((s % 3600) / 60), sec: s % 60 };
        Object.entries(v).forEach(([k, n]) => {
          const c = $(`[data-cd="${k}"]`, el);
          if (c) c.textContent = pad(n);
        });
        el.setAttribute('aria-label', `${v.days} days ${v.hrs} hours ${v.min} minutes to go`);
      };
      tick();
      setInterval(tick, 1000);
    });
  }

  // ---------- product page ----------
  function initProduct(root = document) {
    $$('[data-product]', root).forEach((pd) => {
      const form = $('[data-product-form]', pd) || $('form[action*="/cart/add"]', pd);
      const input = $('[data-variant-input]', pd);
      const sized = pd.dataset.sized === 'true';
      const nudge = $('[data-size-nudge]', pd);
      const sizes = $('[data-sizes]', pd);
      const label = $('[data-size-label]', pd);
      const stickyBtn = $('[data-sticky-add]', pd);
      const code = pd.dataset.prepaid;
      const pickedId = () => Number(input?.value || 0);
      const doNudge = () => {
        if (!sizes) return;
        nudge.hidden = false;
        sizes.classList.remove('tm-shake');
        void sizes.offsetWidth;
        sizes.classList.add('tm-shake');
        sizes.scrollIntoView({ behavior: 'smooth', block: 'center' });
      };
      $$('[data-size]', pd).forEach((b) =>
        b.addEventListener('click', () => {
          input.value = b.dataset.size;
          $$('[data-size]', pd).forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
          if (label) label.textContent = `: ${b.dataset.sizeTitle}`;
          if (nudge) nudge.hidden = true;
          const buy = $('[data-buy-now]', pd); // looked up fresh: checkout apps swap the link for a copy
          if (buy) buy.href = `${TM.routes.cart}/${b.dataset.size}:1${code ? `?discount=${encodeURIComponent(code)}` : ''}`;
          const ss = $('[data-sticky-size]', pd);
          if (ss) ss.textContent = ` · ${b.dataset.sizeTitle}`;
          if (stickyBtn) stickyBtn.textContent = stickyBtn.dataset.add;
          // real inventory only, and only at or below the threshold set in Theme settings
          const sn = $('[data-stock-note]', pd);
          if (sn) {
            const left = b.dataset.stock === undefined ? NaN : Number(b.dataset.stock);
            const show = TM.lowStock > 0 && left > 0 && left <= TM.lowStock;
            sn.hidden = !show;
            sn.textContent = show ? `Only ${left} left in ${b.dataset.sizeTitle}` : '';
          }
          track('select_size', { item: pd.dataset.handle, size: b.dataset.sizeTitle });
        }),
      );
      pd.addEventListener('tm:pick-size', doNudge); // sent by the click guard in theme.liquid
      form?.addEventListener('submit', (e) => {
        e.preventDefault();
        if (!pickedId()) return doNudge();
        cart.add([{ id: pickedId(), quantity: 1 }], 'pdp', form.querySelector('[type=submit]'));
      });
      stickyBtn?.addEventListener('click', () => (pickedId() ? form.requestSubmit() : doNudge()));
      // sticky bar: once the buy buttons have scrolled away above. The root runs far below the screen, so the
      // only crossing is the top edge: a fast fling or a restored scroll position can't jump past it unseen.
      const atc = $('[data-atc]', pd);
      const sticky = $('[data-sticky-buy]', pd);
      if (atc && sticky && 'IntersectionObserver' in window)
        new IntersectionObserver(([e]) => sticky.setAttribute('aria-hidden', String(e.isIntersecting)), { rootMargin: '0px 0px 100000px 0px' }).observe(atc);
      // tabs
      $$('[data-tab]', pd).forEach((t) =>
        t.addEventListener('click', () => {
          $$('[data-tab]', pd).forEach((x) => x.setAttribute('aria-selected', String(x === t)));
          $$('[data-panel]', pd).forEach((p) => (p.hidden = p.dataset.panel !== t.dataset.tab));
        }),
      );
      // size guide
      const guide = $('[data-size-guide]', pd);
      if (guide && guide.showModal) {
        $('[data-size-guide-open]', pd)?.addEventListener('click', () => {
          guide.showModal();
          track('size_guide', { item: pd.dataset.handle });
        });
        $('[data-size-guide-close]', guide)?.addEventListener('click', () => guide.close());
        guide.addEventListener('click', (e) => e.target === guide && guide.close());
        // one unit switch for every size table on the page (sheet + Size & fit tab)
        $$('[data-unit]', pd).forEach((u) =>
          u.addEventListener('click', () => {
            $$('[data-unit]', pd).forEach((x) => x.setAttribute('aria-pressed', String(x.dataset.unit === u.dataset.unit)));
            $$('td[data-in]', pd).forEach((c) => (c.textContent = c.dataset[u.dataset.unit]));
          }),
        );
      }
      // swipe gallery counter
      const gt = $('[data-gallery-track]', pd);
      gt?.addEventListener('scroll', () => ($('[data-gallery-index]', pd).textContent = Math.round(gt.scrollLeft / gt.clientWidth) + 1), { passive: true });
      // lightbox
      const lb = $('[data-lightbox-view]', pd);
      const imgs = JSON.parse($('[data-product-images]', pd)?.textContent || '[]');
      let li = 0;
      const show = (n) => {
        li = (n + imgs.length) % imgs.length;
        $('[data-lightbox-img]', lb).src = imgs[li];
        $('[data-lightbox-count]', lb).textContent = `${li + 1} / ${imgs.length}`;
      };
      const lbOpen = (n) => (show(n), lb.setAttribute('aria-hidden', 'false'), lock(true), $('[data-lightbox-close]', lb).focus());
      const lbClose = () => lb.getAttribute('aria-hidden') === 'false' && (lb.setAttribute('aria-hidden', 'true'), lock(false));
      $$('[data-lightbox]', pd).forEach((b) => b.addEventListener('click', () => lbOpen(Number(b.dataset.lightbox))));
      $('[data-lightbox-close]', lb)?.addEventListener('click', lbClose);
      $('[data-lightbox-prev]', lb)?.addEventListener('click', () => show(li - 1));
      $('[data-lightbox-next]', lb)?.addEventListener('click', () => show(li + 1));
      addEventListener('keydown', (e) => {
        if (lb?.getAttribute('aria-hidden') !== 'false') return;
        if (e.key === 'Escape') lbClose();
        if (e.key === 'ArrowRight') show(li + 1);
        if (e.key === 'ArrowLeft') show(li - 1);
      });
      fillArrival(pd);
      track('view_item', { item: pd.dataset.handle });
    });
  }

  // ---------- recommendations ----------
  function initRecommendations(root = document) {
    $$('[data-recommendations]', root).forEach(async (box) => {
      if (box.children.length || !box.dataset.url) return;
      try {
        const html = await (await fetch(box.dataset.url)).text();
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
        if (fresh && fresh.children.length) {
          box.innerHTML = fresh.innerHTML;
          initCards(box);
          initRails(box);
          initReveal(box);
          saved.paint(box);
        }
      } catch (e) {}
    });
  }

  // ---------- collection filters ----------
  function initCollection(root = document) {
    $$('[data-collection]', root).forEach((c) => {
      let type = '', stock = false;
      const items = $$('[data-item]', c);
      const apply = () => {
        let n = 0;
        items.forEach((li) => {
          const on = (!type || li.dataset.type === type) && (!stock || li.dataset.available === 'true');
          li.hidden = !on;
          if (on) n++;
        });
        const cnt = $('[data-count]', c);
        if (cnt) cnt.textContent = n;
        $('[data-empty]', c).hidden = n > 0;
      };
      if (c.hasAttribute('data-only')) apply();
      $$('[data-filter-type]', c).forEach((b) =>
        b.addEventListener('click', () => {
          type = b.dataset.filterType === type ? '' : b.dataset.filterType;
          $$('[data-filter-type]', c).forEach((x) => x.setAttribute('aria-pressed', String(x.dataset.filterType === type)));
          apply();
        }),
      );
      $('[data-filter-stock]', c)?.addEventListener('click', (e) => {
        stock = !stock;
        e.currentTarget.setAttribute('aria-pressed', String(stock));
        apply();
      });
      $('[data-sort]', c)?.addEventListener('change', (e) => {
        const u = new URL(location.href);
        u.searchParams.set('sort_by', e.target.value);
        location.href = u.toString();
      });
    });
  }

  // ---------- light analytics hooks ----------
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-track]');
    if (t) track(t.dataset.track);
  });

  // ---------- tiger transition into Founder ----------
  function initTiger() {
    const layer = $('#tm-tiger');
    if (!layer) return;
    const fu = new URL(TM.founderUrl || '/pages/founder', location.href);
    const isFounder = (u) => u.pathname.replace(/\/$/, '') === fu.pathname.replace(/\/$/, '') && (!fu.search || u.search.includes(fu.search.slice(1)));
    const onFounder = () => isFounder(location);
    // arriving: pull the sumi wipe away to reveal the Founder page
    if (document.documentElement.classList.contains('tm-tiger-in')) {
      const done = () => {
        document.documentElement.classList.remove('tm-tiger-in');
        layer.classList.add('hidden');
        layer.style.clipPath = '';
      };
      if (reduce() || !layer.animate) done();
      else
        requestAnimationFrame(() =>
          layer.animate([{ clipPath: 'inset(0 0% 0 0%)' }, { clipPath: 'inset(0 0% 0 100%)' }], { duration: 420, delay: 140, easing: 'cubic-bezier(0.4,0,0.6,1)', fill: 'forwards' }).finished.then(done, done),
        );
    }
    if (!TM.tiger || !TM.tiger.on || TM.design) return;
    setTimeout(() => [TM.tiger.stride, TM.tiger.leap].forEach((s) => (new Image().src = s)), 2500);
    let busy = false;
    document.addEventListener(
      'click',
      (e) => {
        if (busy || reduce() || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const a = e.target.closest('a[href]');
        if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
        const url = new URL(a.href, location.href);
        if (url.origin !== location.origin || !isFounder(url) || onFounder()) return;
        e.preventDefault();
        busy = true;
        const slow = new URLSearchParams(location.search).get('tiger') === 'slow';
        const ms = slow ? 650 * 8 : 650;
        pounce(layer, ms);
        const l = document.createElement('link');
        l.rel = 'prefetch';
        l.href = url.href;
        document.head.appendChild(l);
        setTimeout(() => {
          try {
            sessionStorage.setItem('tm-tiger', '1');
          } catch (err) {}
          location.href = url.href;
        }, ms);
      },
      true,
    );
    addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      busy = false;
      layer.innerHTML = '';
      layer.classList.add('hidden');
    });
  }

  // the hunt: creeps in low, coils, then one long pounce across the screen; the sumi wipe follows its body
  function pounce(layer, ms) {
    const vw = innerWidth, vh = innerHeight;
    const W = Math.min(vw * 0.72, vh * 0.68);
    const H = (W * 420) / 1000;
    const lift = Math.min(vh * 0.12, 110);
    layer.innerHTML = `
      <div data-wipe class="absolute inset-y-0 left-0 bg-sumi" style="width:${vw * 3}px"></div>
      <div data-run class="absolute left-0" style="top:${vh * 0.52 - H / 2}px;width:${W}px;height:${H}px">
        <div data-body class="relative size-full" style="transform-origin:55% 75%">
          <img data-ghost src="${TM.tiger.leap}" alt="" class="absolute inset-0 size-full -translate-x-[7%] select-none object-contain blur-[5px]" style="opacity:0">
          <div class="absolute inset-0" style="filter:drop-shadow(0 22px 30px rgb(0 0 0 / .5))">
            <img data-stride src="${TM.tiger.stride}" alt="" class="absolute inset-0 size-full select-none object-contain">
            <img data-leap src="${TM.tiger.leap}" alt="" class="absolute inset-0 size-full select-none object-contain" style="opacity:0">
          </div>
        </div>
      </div>`;
    layer.classList.remove('hidden');
    const run = $('[data-run]', layer), wipe = $('[data-wipe]', layer), body = $('[data-body]', layer);
    const xs = [-W, -W * 0.38, vw + W * 0.12];
    const ease1 = 'cubic-bezier(0.25,0.6,0.35,1)', ease2 = 'cubic-bezier(0.45,0.05,0.7,0.9)';
    const opt = { duration: ms, fill: 'forwards' };
    run.animate(
      [
        { transform: `translateX(${xs[0]}px)`, offset: 0, easing: ease1 },
        { transform: `translateX(${xs[1]}px)`, offset: 0.3, easing: ease2 },
        { transform: `translateX(${xs[2]}px)`, offset: 1 },
      ],
      opt,
    );
    const wx = (x) => `translateX(${x + W * 0.55 - vw * 3}px)`;
    wipe.animate(
      [
        { transform: wx(xs[0]), offset: 0, easing: ease1 },
        { transform: wx(xs[1]), offset: 0.3, easing: ease2 },
        { transform: wx(xs[2]), offset: 1 },
      ],
      opt,
    );
    const at = [0, 0.24, 0.3, 0.62, 1];
    const ys = [H * 0.06, H * 0.1, H * 0.04, -lift, -lift * 0.35];
    const rs = [0, 0, -5, 0, 6], sx = [1, 0.98, 1.05, 1.02, 1], sy = [1, 0.93, 1, 1, 1];
    body.animate(
      at.map((o, k) => ({ offset: o, transform: `translateY(${ys[k]}px) rotate(${rs[k]}deg) scale(${sx[k]}, ${sy[k]})`, easing: 'ease-in-out' })),
      opt,
    );
    const fade = (el, vals) => el.animate(at.map((o, k) => ({ offset: o, opacity: vals[k] })), opt);
    fade($('[data-stride]', layer), [1, 1, 0, 0, 0]);
    fade($('[data-leap]', layer), [0, 0, 1, 1, 1]);
    fade($('[data-ghost]', layer), [0, 0, 0.22, 0.12, 0]);
  }

  // ---------- boot ----------
  function initSection(root) {
    initAnnounce(root);
    initMarquees(root);
    initReveal(root);
    initFilms(root);
    initRails(root);
    initCards(root);
    initStories(root);
    initSlides(root);
    initKit(root);
    initCountdowns(root);
    initProduct(root);
    initRecommendations(root);
    initCollection(root);
    saved.paint(root);
  }
  function boot() {
    initHeader();
    initSearch();
    initTiger();
    initSection(document);
    cart.wire();
    // the bag page is a fallback: the drawer carries the add-ons, savings and the code
    if (location.pathname === TM.routes.cart) cart.open();
    fillArrival();
    // hero text waits for the loader to finish (CSS pauses it while html.tm-intro is set)
  }
  // off-screen sections (data-lazy-section) come from the page's ?view=lazy template in one request, as the first nears the screen
  function initLazySections() {
    const holes = $$('[data-lazy-section]');
    if (!holes.length) return;
    let done = false;
    const load = () => {
      if (done) return;
      done = true;
      fetch(location.pathname + '?view=lazy')
        .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          holes.forEach((h) => {
            const src = doc.querySelector(`[id$="__${h.dataset.lazySection}"]`);
            const el = src && src.firstElementChild;
            if (!el) return h.remove();
            h.replaceWith(el);
            initSection(el);
          });
        })
        .catch(() => holes.forEach((h) => h.remove()));
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((es) => es.some((e) => e.isIntersecting) && (io.disconnect(), load()), { rootMargin: '1500px 0px' });
      holes.forEach((h) => io.observe(h));
    }
    // and in any case once the page has settled, so a fast scroll never finds a gap
    addEventListener('load', () => setTimeout(load, 2500), { once: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => (boot(), initLazySections()));
  else (boot(), initLazySections());
  // theme editor: re-wire a section after it's edited
  document.addEventListener('shopify:section:load', (e) => initSection(e.target));
  window.TMCart = cart;
})();
